/**
 * AdminPermissionsPage — Sub-perfis Administrativos (Gestão de Admins).
 *
 * Pacote 6 — Spec: docs/V2/07_REGRAS_NEGOCIO.md §15
 *
 * Lista admins existentes com permissões agregadas por área e permite
 * editar/revogar via Drawer. Admin Master = full nos 4 níveis da área "general"
 * (limite hard de 3 contas, validado no backend).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import adminPermissionsV2API from '../../services/api/v2/adminPermissions';
import AdminPermissionsDrawer from './AdminPermissionsDrawer';
import NewAdminDrawer from './NewAdminDrawer';

const AREA_ORDER = ['general', 'tickets', 'financial', 'administrative'];
const AREA_LABELS = {
  general: 'Geral',
  tickets: 'Suporte',
  financial: 'Financeiro',
  administrative: 'Administrativo',
};

/** Calcula a "altura" do nível de permissão concedido na área. */
const getAreaLevel = (perm) => {
  if (!perm) return 'none';
  if (perm.can_view && perm.can_edit && perm.can_approve && perm.can_delete) {
    return 'full';
  }
  if (perm.can_view && perm.can_edit && perm.can_approve) return 'approve';
  if (perm.can_view && perm.can_edit) return 'edit';
  if (perm.can_view) return 'view';
  return 'none';
};

/** Chip por área, com cor proporcional ao nível concedido. */
const AreaChip = ({ area, perm, isAdminMaster }) => {
  const level = getAreaLevel(perm);
  const palette = {
    none: { bg: '#f3f4f6', fg: '#6b7280', border: '#e5e7eb' },
    view: { bg: '#dbeafe', fg: '#1d4ed8', border: '#bfdbfe' },
    edit: { bg: '#bfdbfe', fg: '#1e40af', border: '#93c5fd' },
    approve: { bg: '#dcfce7', fg: '#166534', border: '#bbf7d0' },
    full: { bg: '#bbf7d0', fg: '#065f46', border: '#86efac' },
  };
  // Destaque visual para Admin Master (full em "general")
  const isMaster = area === 'general' && level === 'full' && isAdminMaster;
  const colors = isMaster
    ? { bg: '#ede9fe', fg: '#6d28d9', border: '#c4b5fd' }
    : palette[level];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: colors.bg,
        color: colors.fg,
        border: `1px solid ${colors.border}`,
        whiteSpace: 'nowrap',
      }}
      title={`${AREA_LABELS[area]} — nível ${level}`}
    >
      {isMaster && <Icon icon="mdi:shield-crown" width="12" />}
      {AREA_LABELS[area]}
      {level === 'none' ? '' : ` · ${level}`}
    </span>
  );
};

/** Detecta se admin é Admin Master (full em "general"). */
const isAdminMaster = (admin) => {
  const general = (admin?.permissions || []).find((p) => p.area === 'general');
  return !!(
    general
    && general.can_view
    && general.can_edit
    && general.can_approve
    && general.can_delete
  );
};

const STATUS_LABELS = {
  active: { label: 'Ativo', bg: '#dcfce7', fg: '#166534' },
  pending: { label: 'Pendente', bg: '#fef3c7', fg: '#92400e' },
  suspended: { label: 'Suspenso', bg: '#fee2e2', fg: '#991b1b' },
  inactive: { label: 'Inativo', bg: '#f3f4f6', fg: '#6b7280' },
};

const AdminPermissionsPage = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [drawerAdmin, setDrawerAdmin] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  // Pacote 8 — drawer "Novo Admin"
  const [newAdminOpen, setNewAdminOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await adminPermissionsV2API.listAdmins();
      setAdmins(res?.data?.admins || []);
    } catch (err) {
      const msg = err?.message
        || err?.response?.data?.error
        || 'Erro ao carregar lista de administradores.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openDrawer = (admin) => {
    setDrawerAdmin(admin);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    // pequeno delay pro fade-out do drawer antes de limpar o admin
    setTimeout(() => setDrawerAdmin(null), 250);
  };

  const handleSaved = () => {
    load();
  };

  const handleRevoke = async (admin) => {
    if (!admin?.user_id) return;
    const confirmed = window.confirm(
      `Tem certeza que deseja revogar TODAS as permissões de "${admin.name || admin.email}"?\n\n`
      + 'Esta ação remove o acesso administrativo deste usuário em todas as áreas.',
    );
    if (!confirmed) return;

    try {
      setRevokingId(admin.user_id);
      await adminPermissionsV2API.revokeAll(admin.user_id);
      toast.success(`Permissões de ${admin.name || admin.email} revogadas.`);
      await load();
    } catch (err) {
      const msg = err?.message
        || err?.response?.data?.error
        || 'Erro ao revogar permissões.';
      toast.error(msg);
    } finally {
      setRevokingId(null);
    }
  };

  const filtered = admins.filter((a) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      a.name?.toLowerCase().includes(q)
      || a.email?.toLowerCase().includes(q)
    );
  });

  const masterCount = admins.filter(isAdminMaster).length;

  return (
    <div className="admin-permissions-page" style={{ padding: 24, width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <AdminPageHeader
          icon="mdi:shield-account"
          title="Sub-perfis Administrativos"
          subtitle="Defina o que cada administrador pode fazer por área."
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setNewAdminOpen(true)}
          style={{ whiteSpace: 'nowrap' }}
        >
          <Icon icon="mdi:account-plus" width="16" style={{ marginRight: 6 }} />
          Novo Admin
        </button>
      </div>
      {/* Mantém um page-title escondido caso a navegação acessível precise dele */}
      <span className="page-title sr-only" style={{ position: 'absolute', left: -9999 }}>
        Sub-perfis Administrativos
      </span>

      <div
        className="alert alert-info d-flex align-items-start gap-2"
        role="status"
        style={{ marginTop: 16, marginBottom: 16 }}
      >
        <Icon icon="mdi:information-outline" width="20" />
        <span>
          Admins existentes herdam todas as permissões automaticamente. Apenas
          <strong> Admin Masters</strong> podem editar permissões — limite hard
          de <strong>3 contas</strong> com nível total na área <strong>Geral</strong>.
          {' '}
          <span style={{ color: '#6d28d9', fontWeight: 600 }}>
            Atualmente: {masterCount}/3 Admin Masters.
          </span>
        </span>
      </div>

      <div className="card shadow-sm" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Buscar admin</label>
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0">
                  <Icon icon="mdi:magnify" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder="Nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-6 d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={load}
                disabled={loading}
              >
                <Icon icon="mdi:refresh" />
                {' '}
                Atualizar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
              <span
                className="d-block text-muted"
                style={{ marginTop: 12 }}
              >
                Carregando administradores...
              </span>
            </div>
          ) : error ? (
            <div className="text-center py-5">
              <Icon icon="mdi:alert-circle" width="48" className="text-danger" />
              <span className="d-block text-danger mt-3">{error}</span>
              <button
                type="button"
                className="btn btn-outline-danger mt-3"
                onClick={load}
              >
                Tentar novamente
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-5">
              <Icon icon="mdi:account-off-outline" width="48" className="text-muted" />
              <span className="d-block text-muted mt-3">
                {searchTerm
                  ? 'Nenhum administrador encontrado para a busca.'
                  : 'Nenhum administrador cadastrado ainda.'}
              </span>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col" style={{ minWidth: 200 }}>Nome</th>
                    <th scope="col" style={{ minWidth: 220 }}>Email</th>
                    <th scope="col" style={{ minWidth: 110 }}>Status</th>
                    <th scope="col" style={{ minWidth: 360 }}>Permissões</th>
                    <th scope="col" style={{ minWidth: 220, textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((admin) => {
                    const master = isAdminMaster(admin);
                    const status = STATUS_LABELS[admin.status] || STATUS_LABELS.active;
                    const permsByArea = (admin.permissions || []).reduce(
                      (acc, p) => ({ ...acc, [p.area]: p }),
                      {},
                    );

                    return (
                      <tr key={admin.user_id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                background: master ? '#ede9fe' : '#e0e7ff',
                                color: master ? '#6d28d9' : '#3730a3',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                fontSize: 13,
                              }}
                            >
                              {(admin.name || admin.email || '?').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <span style={{ fontWeight: 600, display: 'block' }}>
                                {admin.name || 'Sem nome'}
                              </span>
                              {master && (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 3,
                                    fontSize: 11,
                                    color: '#6d28d9',
                                    fontWeight: 600,
                                  }}
                                >
                                  <Icon icon="mdi:shield-crown" width="12" />
                                  Admin Master
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span style={{ color: '#374151' }}>{admin.email}</span>
                        </td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              background: status.bg,
                              color: status.fg,
                              fontWeight: 600,
                            }}
                          >
                            {status.label}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {AREA_ORDER.map((area) => (
                              <AreaChip
                                key={area}
                                area={area}
                                perm={permsByArea[area]}
                                isAdminMaster={master}
                              />
                            ))}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div className="d-inline-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openDrawer(admin)}
                            >
                              <Icon icon="mdi:pencil" />
                              {' '}
                              Editar
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRevoke(admin)}
                              disabled={revokingId === admin.user_id}
                            >
                              {revokingId === admin.user_id ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-1" role="status" />
                                  Revogando...
                                </>
                              ) : (
                                <>
                                  <Icon icon="mdi:shield-off-outline" />
                                  {' '}
                                  Revogar acesso
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <AdminPermissionsDrawer
        isOpen={drawerOpen}
        onClose={closeDrawer}
        admin={drawerAdmin}
        onSaved={handleSaved}
      />

      {/* Pacote 8 — Drawer "Novo Admin" (promove user existente a admin) */}
      <NewAdminDrawer
        isOpen={newAdminOpen}
        onClose={() => setNewAdminOpen(false)}
        onCreated={() => {
          setNewAdminOpen(false);
          load();
        }}
      />
    </div>
  );
};

export default AdminPermissionsPage;
